'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BfsiHero from './BfsiHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from BfsiHero
   (FEATURED + STATS[0] + STATS[1]), verbatim values. */
const METRICS = [
  { value: '13.34L', label: 'Complaints filed with RBI Ombudsman in FY25 — up 13.55% YoY (RBI Annual Report 2024–25)' },
  { value: '68%',    label: 'Of BFSI CX leaders cite customer service as top grievance source (Kapture CX Survey)' },
  { value: '32%',    label: 'Of customers leave after just one bad experience (PwC)' },
];

export default function BfsiBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('bfsi');

  const CHALLENGES = [
    {
      title: t('industries_bfsi.the_30_day_clock'),
      desc:  t('industries_bfsi.rbi_ombudsman_complaint_escalation_2'),
    },
    {
      title: t('industries_bfsi.the_fraud_signal_nobody_saw'),
      desc:  t('industries_bfsi.unauthorised_debit_account_hacked_w'),
    },
    {
      title: t('industries_bfsi.three_teams_one_frustrated_customer'),
      desc:  t('industries_bfsi.savings_account_home_loan_credit_ca'),
    },
    {
      title: t('industries_bfsi.the_switch_that_happened_in_silence'),
      desc:  t('industries_bfsi.customers_announce_switches_to_neo'),
    },
  ];

  const FAQS = [
    {
      q: t('industries_bfsi.what_is_a_bfsi_customer_experience'),
      a: t('industries_bfsi.one_workspace_complaint_management'),
    },
    {
      q: t('industries_bfsi.how_does_locobuzz_prevent_complaint'),
      a: t('industries_bfsi.five_automated_steps_intercept_clas'),
    },
    {
      q: t('industries_bfsi.can_locobuzz_monitor_fraud_signals'),
      a: t('industries_bfsi.yes_customisable_fraud_signal_monit'),
    },
    {
      q: t('industries_bfsi.how_does_locobuzz_use_agentic_ai_fo'),
      a: t('industries_bfsi.agentiq_doesnt_just_route_it_acts_c'),
    },
    {
      q: t('industries_bfsi.does_locobuzz_support_rbi_and_irdai'),
      a: t('industries_bfsi.yes_rbi_integrated_ombudsman_scheme'),
    },
    {
      q: t('industries_bfsi.which_channels_does_locobuzz_monito'),
      a: t('industries_bfsi.20_channels_twitter_x_facebook_link'),
    },
    {
      q: t('industries_bfsi.how_does_locobuzz_support_hyper_per'),
      a: t('industries_bfsi.360_customer_profiles_across_saving'),
    },
    {
      q: t('industries_bfsi.can_locobuzz_integrate_with_existin'),
      a: t('industries_bfsi.yes_salesforce_zoho_dynamics_zendes'),
    },
  ];

  return (
    <div className="industry-page-wrap">
      {/* AEO conversational seeds — in HTML source, never rendered visibly */}
      <span className="sr-only">
        {t('industries_bfsi.locobuzz_is_an_indian_ainative_cust')}
      </span>
      <span className="sr-only">
        {t('industries_bfsi.in_bfsi_every_unresolved_complaint')}
      </span>
      <span className="sr-only">
        {t('industries_bfsi.locobuzz_maps_six_purposebuilt_capa')}
      </span>
      <span className="sr-only">
        {t('industries_bfsi.banks_nbfcs_and_insurers_using_loco')}
      </span>

      <BfsiHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t('industries_bfsi.why_cx_is_a_compliance_issue')}
        title={t('industries_bfsi.why_is_bfsi_customer_experience_a_c')}
        paragraph={t('industries_bfsi.the_rbi_30day_clock_starts_the_mome')}
        imageSrc="/industries/why/bfsi.webp"
        imageAlt="BFSI customer experience compliance and complaint management dashboard"
        bg="tint"
      />

      <IndustryChallenges
        title={t('industries_bfsi.what_challenges_do_bfsi_cx_teams_fa')}
        items={CHALLENGES}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="bfsi-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
